// SidebarLayout.js

'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, Menu, MenuButton, MenuItem, MenuItems, TransitionChild } from '@headlessui/react';
import { Bars3Icon, BellIcon, CalendarIcon, ChartPieIcon, Cog6ToothIcon, DocumentDuplicateIcon, FolderIcon, HomeIcon, UsersIcon, XMarkIcon, CloudArrowUpIcon, CurrencyDollarIcon, ClipboardDocumentCheckIcon, PlusCircleIcon, ShoppingCartIcon, BanknotesIcon, TruckIcon, LightBulbIcon, ClipboardDocumentListIcon, CubeIcon, ClockIcon, PrinterIcon, Squares2X2Icon, ScaleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Link, useLocation } from 'react-router-dom';
import { getUserRoles, getUserName } from './auth';
import NotificationBell from './components/NotificationBell';
import ApiClient from './utils/ApiClient';
import { badgeLabel, badgeTitle } from './utils/reorderBadge';

const allNavigation = [
    { name: 'Dashboard', to: '/admin-dashboard', icon: HomeIcon, roles: ['ADMIN'] },
    { name: 'Add ASIN Listings', to: '/add-asin-listings', icon: PlusCircleIcon, roles: ['ADMIN'] },
    { name: 'Returns', to: '/returns', icon: FolderIcon, roles: ['ADMIN'] },
    { name: 'Case Review', to: '/case-review', icon: ClipboardDocumentCheckIcon, roles: ['ADMIN', 'CASE_REVIEWER'] },
    { name: 'Documents', to: '/documents', icon: DocumentDuplicateIcon, roles: ['ADMIN'] },
    { name: 'Reports', to: '/reports', icon: ChartPieIcon, roles: ['ADMIN'] },
    { name: 'Feed Status', to: '/feed-status', icon: CloudArrowUpIcon, roles: ['ADMIN'] },
    { name: 'System Status', to: '/admin/status', icon: ChartPieIcon, roles: ['ADMIN'] },
    { name: 'Index Advisor', to: '/index-advisor', icon: ChartPieIcon, roles: ['ADMIN'] },
    { name: 'Price Lists', to: '/price-lists', icon: CurrencyDollarIcon, roles: ['ADMIN'] },
    { name: 'Shipment Planner', to: '/shipment-planner', icon: TruckIcon, roles: ['ADMIN'] },
    { name: 'Shipment Planner V2', to: '/shipment-planner-v2', icon: TruckIcon, roles: ['ADMIN'] },
    { name: 'ASIN Aliases', to: '/asin-aliases', icon: TruckIcon, roles: ['ADMIN'] },
    { name: 'Shipment Runs', to: '/shipment-runs', icon: ClockIcon, roles: ['ADMIN'] },
    { name: 'Split Sets', to: '/split-sets', icon: Squares2X2Icon, roles: ['ADMIN'] },
    { name: 'Confirmed Shipments', to: '/confirmed-shipments', icon: PrinterIcon, roles: ['ADMIN'] },
    { name: 'Shipment Queue', to: '/shipment-queue', icon: ClipboardDocumentListIcon, roles: ['ADMIN'] },
    { name: 'Business Insights', to: '/business-insights', icon: LightBulbIcon, roles: ['ADMIN'] },
    { name: 'PO Creation', to: '/po-creation', icon: ClipboardDocumentListIcon, roles: ['ADMIN'] },
    { name: 'Shipping Coach', to: '/shipping-coach', icon: TruckIcon, roles: ['ADMIN'] },
    { name: 'Product Size Tier', to: '/product-size', icon: CubeIcon, roles: ['ADMIN'] },
    { name: 'Amazon Ads', to: '/amazon-ads', icon: CurrencyDollarIcon, roles: ['ADMIN'] },
    { name: 'Seller Legal Tracker', to: '/seller-legal-tracker', icon: ScaleIcon, roles: ['ADMIN'] },
    { name: 'War Room', to: '/war-room', icon: ChartPieIcon, roles: ['ADMIN'] },
    { name: 'War Room Planning', to: '/war-room/planning', icon: ChartPieIcon, roles: ['ADMIN'] },
    { name: 'War Room FC Leaderboard', to: '/war-room/fc-leaderboard', icon: ChartPieIcon, roles: ['ADMIN'] },
];

const allSfi = [
    { id: 1, name: 'SF Inventory', href: '/merchantfulfilledinventory', icon: UsersIcon, roles: ['ADMIN'] },
    { id: 2, name: 'SF Orders', href: '/sellerfulfilledorders', icon: CalendarIcon, roles: ['ADMIN'] },
    { id: 3, name: 'Receive Inventory', href: '/receiveinventory', icon: BellIcon, roles: ['ADMIN'] },
    { id: 4, name: 'Upload PO', href: '/receiveinventory/uploadpo', icon: Bars3Icon, roles: ['ADMIN'] },
    { id: 5, name: 'Po/Packing List', href: '/polist', icon: Bars3Icon, roles: ['ADMIN'] },
    { id: 6, name: 'PO Status List', href: '/packinglists', icon: Bars3Icon, roles: ['ADMIN'] },
    { id: 8, name: 'Local Inventory Adjustment', href: '/localinventoryadjustment', icon: UsersIcon, roles: ['ADMIN'] },
    { id: 9, name: 'Local Inventory Audit', href: '/localinventoryaudit', icon: Cog6ToothIcon, roles: ['ADMIN'] },
    { id: 10, name: 'Audit Review', href: '/auditreview', icon: ClipboardDocumentCheckIcon, roles: ['ADMIN'] },
    { id: 11, name: 'Local Inventory Report', href: '/localinventoryreport', icon: ChartPieIcon, roles: ['ADMIN'] },
    { id: 13, name: 'Manage SFP Inventory', href: '/manage-sfp-inventory', icon: CubeIcon, roles: ['ADMIN'] },
    { id: 12, name: 'Workcation', href: '#', initial: 'W', roles: ['ADMIN'] },
];

const allAccounting = [
    { id: 1, name: 'Purchasing', href: '/purchasing', icon: ShoppingCartIcon, roles: ['ADMIN'] },
    { id: 2, name: 'PO/Invoice/Credits List', href: '/poinvoicecredits', icon: BanknotesIcon, roles: ['ADMIN'] },
    { id: 3, name: 'Xero Sync', href: '/xero-documents', icon: CloudArrowUpIcon, roles: ['ADMIN'] },
];

const handleLogout = () => {
    // Import ApiClient dynamically to avoid circular dependencies
    const ApiClient = require('./utils/ApiClient').default;
    // Clear all auth data (jwt and refreshToken)
    ApiClient.clearAuth();
    console.log("User logged out.");
    // Redirect to login page
    window.location.href = '/';
};


const userNavigation = [
    { name: 'Your profile', href: '#' },
    { name: 'Sign out', onClick: handleLogout},
];

// Helper function to apply conditional classes
function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function SidebarLayout({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation(); // to get the current path

    // Task 25 (action queue): count badge on the "Shipment Runs" nav entry when the action queue
    // has anything worth a look (TAKE decision or a "needed FC offered" signal) -- cheap poll,
    // mirrors NotificationBell's fetch-count-only pattern (no full queue payload here).
    const [shipmentRunsBadgeCount, setShipmentRunsBadgeCount] = useState(0);
    useEffect(() => {
        let cancelled = false;
        const fetchBadge = async () => {
            try {
                const res = await ApiClient.get('/api/shipment-planner/action-queue');
                if (cancelled) return;
                const items = res.data || [];
                const count = items.filter(i => i.decision === 'TAKE' || (i.neededFcHits && i.neededFcHits.length > 0)).length;
                setShipmentRunsBadgeCount(count);
            } catch (err) {
                // Non-critical -- badge just stays at 0 if this fails.
            }
        };
        fetchBadge();
        const interval = setInterval(fetchBadge, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(interval); };
    }, []);

    // PO Creation badge (owner 2026-10-25): the $ amount that should be reordered now (default view).
    const [reorderBadge, setReorderBadge] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const fetchReorderBadge = async () => {
            try {
                const res = await ApiClient.get('/api/po-creation/reorder-badge');
                if (!cancelled) setReorderBadge(res.data);
            } catch (err) {
                // Non-critical -- no badge if this fails.
            }
        };
        fetchReorderBadge();
        const interval = setInterval(fetchReorderBadge, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(interval); };
    }, []);
    const reorderBadgeLabel = badgeLabel(reorderBadge);

    // Get user roles and filter navigation items based on role access
    const userRoles = getUserRoles();
    const navigation = allNavigation.filter(item =>
        item.roles.some(role => userRoles.includes(role))
    );
    const sfi = allSfi.filter(item =>
        item.roles.some(role => userRoles.includes(role))
    );
    const accounting = allAccounting.filter(item =>
        item.roles.some(role => userRoles.includes(role))
    );

    // Settings should only be visible to ADMIN users
    const showSettings = userRoles.includes('ADMIN');

    // Get user's display name from JWT
    const userName = getUserName() || 'User';

    return (
        <>
            <div>
                <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
                    <DialogBackdrop
                        transition
                        className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
                    />

                    <div className="fixed inset-0 flex">
                        <DialogPanel
                            transition
                            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
                        >
                            <TransitionChild>
                                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                                    <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                                        <span className="sr-only">Close sidebar</span>
                                        <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                                    </button>
                                </div>
                            </TransitionChild>
                            {/* Sidebar component */}
                            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                                <div className="flex h-16 shrink-0 items-center">
                                    <img
                                        alt="Calibrate"
                                        src="/calibrate-logo.svg"
                                        className="h-8 w-auto"
                                    />
                                </div>
                                <nav className="flex flex-1 flex-col">
                                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                                        <li>
                                            <ul role="list" className="-mx-2 space-y-1">
                                                {navigation.map((item) => (
                                                    <li key={item.name}>
                                                        <Link
                                                            to={item.to}
                                                            className={classNames(
                                                                location.pathname === item.to
                                                                    ? 'bg-gray-800 text-white'
                                                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                                                            )}
                                        >
                                                            <item.icon aria-hidden="true" className="h-6 w-6 shrink-0" />
                                                            <span className="flex-1">{item.name}</span>
                                                            {item.name === 'Shipment Runs' && shipmentRunsBadgeCount > 0 && (
                                                                <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white">
                                                                    {shipmentRunsBadgeCount}
                                                                </span>
                                                            )}
                                                            {item.name === 'PO Creation' && reorderBadgeLabel && (
                                                                <span data-testid="po-reorder-badge" title={badgeTitle(reorderBadge)}
                                                                      className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
                                                                    {reorderBadgeLabel}
                                                                </span>
                                                            )}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        <li>
                                            <div className="text-xs font-semibold leading-6 text-gray-400">SF Inventory</div>
                                            <ul role="list" className="-mx-2 mt-2 space-y-1">
                                                {sfi.map((sfi) => (
                                                    <li key={sfi.name}>
                                                        <Link
                                                            to={sfi.href}
                                                            className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                                                        >
                                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white">
                                                                {sfi.initial}
                                                            </span>
                                                            <span className="truncate">{sfi.name}</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        <li>
                                            <div className="text-xs font-semibold leading-6 text-gray-400">Accounting</div>
                                            <ul role="list" className="-mx-2 mt-2 space-y-1">
                                                {accounting.map((item) => (
                                                    <li key={item.name}>
                                                        <Link
                                                            to={item.href}
                                                            className={classNames(
                                                                location.pathname === item.href
                                                                    ? 'bg-gray-800 text-white'
                                                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                                                            )}
                                                        >
                                                            <item.icon aria-hidden="true" className="h-6 w-6 shrink-0" />
                                                            <span className="truncate">{item.name}</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        {showSettings && (
                                            <li className="mt-auto">
                                                <Link
                                                    to="/admin/settings"
                                                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                                                >
                                                    <Cog6ToothIcon aria-hidden="true" className="h-6 w-6 shrink-0" />
                                                    Settings
                                                </Link>
                                            </li>
                                        )}
                                    </ul>
                                </nav>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>

                {/* Static sidebar for desktop */}
                <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
                        <div className="flex h-16 shrink-0 items-center">
                            <img
                                alt="Calibrate"
                                src="/calibrate-logo.svg"
                                className="h-8 w-auto"
                            />
                        </div>
                        <nav className="flex flex-1 flex-col">
                            <ul role="list" className="flex flex-1 flex-col gap-y-7">
                                <li>
                                    <ul role="list" className="-mx-2 space-y-1">
                                        {navigation.map((item) => (
                                            <li key={item.name}>
                                                <Link
                                                    to={item.to}
                                                    className={classNames(
                                                        location.pathname === item.to
                                                            ? 'bg-gray-800 text-white'
                                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                                        'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                                                    )}
                                                >
                                                    <item.icon aria-hidden="true" className="h-6 w-6 shrink-0" />
                                                    <span className="flex-1">{item.name}</span>
                                                    {item.name === 'Shipment Runs' && shipmentRunsBadgeCount > 0 && (
                                                        <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white">
                                                            {shipmentRunsBadgeCount}
                                                        </span>
                                                    )}
                                                    {item.name === 'PO Creation' && reorderBadgeLabel && (
                                                        <span data-testid="po-reorder-badge" title={badgeTitle(reorderBadge)}
                                                              className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
                                                            {reorderBadgeLabel}
                                                        </span>
                                                    )}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                                <li>
                                    <div className="text-xs font-semibold leading-6 text-gray-400">Inventory Management</div>
                                    <ul role="list" className="-mx-2 mt-2 space-y-1">
                                        {sfi.map((sfi) => (
                                            <li key={sfi.name}>
                                                <Link
                                                    to={sfi.href}
                                                    className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                                                >
                                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white">
                                                        {sfi.initial}
                                                    </span>
                                                    <span className="truncate">{sfi.name}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                                <li>
                                    <div className="text-xs font-semibold leading-6 text-gray-400">Accounting</div>
                                    <ul role="list" className="-mx-2 mt-2 space-y-1">
                                        {accounting.map((item) => (
                                            <li key={item.name}>
                                                <Link
                                                    to={item.href}
                                                    className={classNames(
                                                        location.pathname === item.href
                                                            ? 'bg-gray-800 text-white'
                                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                                        'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                                                    )}
                                                >
                                                    <item.icon aria-hidden="true" className="h-6 w-6 shrink-0" />
                                                    <span className="truncate">{item.name}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                                {showSettings && (
                                    <li className="mt-auto">
                                        <Link
                                            to="/admin/settings"
                                            className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400
                                             hover:bg-gray-800 hover:text-white"
                                        >
                                            <Cog6ToothIcon aria-hidden="true" className="h-6 w-6 shrink-0" />
                                            Settings
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </nav>
                    </div>
                </div>

                <div className="lg:pl-72">
                    <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                        <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
                            <span className="sr-only">Open sidebar</span>
                            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
                        </button>

                        <div aria-hidden="true" className="h-6 w-px bg-gray-900/10 lg:hidden" />
                        <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                            <form action="#" method="GET" className="relative flex flex-1">
                                <label htmlFor="search-field" className="sr-only">Search</label>
                                <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400" />
                                <input
                                    id="search-field"
                                    name="search"
                                    type="search"
                                    placeholder="Search..."
                                    className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                                />
                            </form>
                            <div className="flex items-center gap-x-4 lg:gap-x-6">
                                <NotificationBell />
                                <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" />
                                <Menu as="div" className="relative">
                                    <MenuButton className="-m-1.5 flex items-center p-1.5">
                                        <span className="sr-only">Open user menu</span>
                                        <img alt="" src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" className="h-8 w-8 rounded-full bg-gray-50" />
                                        <span className="hidden lg:flex lg:items-center">
                                            <span aria-hidden="true" className="ml-4 text-sm font-semibold leading-6 text-gray-900">{userName}</span>
                                            <ChevronDownIcon aria-hidden="true" className="ml-2 h-5 w-5 text-gray-400" />
                                        </span>
                                    </MenuButton>
                                    <MenuItems transition className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none">
                                        {userNavigation.map((item) => (
                                            <MenuItem key={item.name}>
                                                {item.onClick ? (
                                                    <button
                                                        onClick={item.onClick}
                                                        className="block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900 hover:bg-gray-50"
                                                    >
                                                        {item.name}
                                                    </button>
                                                ) : (
                                                    <a
                                                        href={item.href}
                                                        className="block px-3 py-1 text-sm leading-6 text-gray-900 hover:bg-gray-50"
                                                    >
                                                        {item.name}
                                                    </a>
                                                )}
                                            </MenuItem>
                                        ))}
                                    </MenuItems>
                                </Menu>
                            </div>
                        </div>
                    </div>

                    <main className="py-10">
                        <div className="px-4 sm:px-6 lg:px-8">{children}</div>
                    </main>
                </div>
            </div>
        </>
    );
}
